/* eslint-disable no-console */
/**
 * ConversationView Component
 *
 * Displays the conversation history between user and Solomon agent.
 * Shows messages with streaming support, tool use indicators, and
 * optional extended thinking content (Claude's internal reasoning).
 * Renders ImageMessage components for image-related tool results.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import { cn } from '@pray/shared/utils/styles';

import type { SolomonAgentMessage } from 'hooks/solomon/useSolomonAgent';

import { useSolomonPanelContext } from '../context';
import { ActionApprovalCard } from './ActionApprovalCard';
import { EmptyState } from './EmptyState';
import { ImageMessage } from './ImageMessage';
import { InlineImages } from './InlineImages';
import { MessageToolUses } from './MessageToolUses';
import { type RegenerateOption } from './RegenerateOptions';
import { StreamingMessage } from './StreamingMessage';
import { ThinkingSection } from './ThinkingSection';
import { ToolUseIndicator } from './ToolUseIndicator';
import {
  IMAGE_KEY_TO_SLOT,
  IMAGE_TOOLS,
  parseSlotFromPrompt,
  extractImageUrl,
  parseMarkdownImages,
} from '../utils/conversationUtils';
import { type SlotType, PROFILE_SLOTS } from '../utils/imageUtils';

import styles from './ConversationView.module.scss';

interface ConversationViewProps {
  messages: SolomonAgentMessage[];
  currentResponse: string;
  /** Extended thinking content being streamed */
  currentThinking?: string;
  isStreaming: boolean;
  isThinking: boolean;
  activeToolUse: {
    tool: string;
    toolCallId: string;
    input?: Record<string, unknown>;
  } | null;
  /** Callback to send a regenerate prompt to the agent */
  onRegenerate?: (prompt: string) => void;
  className?: string;
}

/** Track state for image tool results */
interface ImageToolState {
  toolCallId: string;
  imageUrl: string;
  targetSlot: SlotType;
  isSaved: boolean;
  isSaving: boolean;
}

/** Track state for inline markdown images */
interface InlineImageState {
  imageUrl: string;
  isSaved: boolean;
  isSaving: boolean;
}

export function ConversationView({
  messages,
  currentResponse,
  currentThinking,
  isStreaming,
  isThinking,
  activeToolUse,
  onRegenerate,
  className,
}: ConversationViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedThinking, setExpandedThinking] = useState<Record<string, boolean>>({});
  const [imageToolStates, setImageToolStates] = useState<Record<string, ImageToolState>>({});
  const [inlineImageStates, setInlineImageStates] = useState<Record<string, InlineImageState>>({});
  const [showRegenerateFor, setShowRegenerateFor] = useState<string | null>(null);

  const { imageGenFlow, saveImageDirect, pendingApprovals, approveAction, rejectPendingAction } =
    useSolomonPanelContext();

  // Pre-compute the last user prompt for each message index
  const lastUserPromptByIndex = useMemo(() => {
    const lookup: Record<number, string> = {};
    let lastUserPrompt = '';

    messages.forEach((message, index) => {
      if (message.role === 'user') {
        lastUserPrompt = message.content;
      }
      lookup[index] = lastUserPrompt;
    });

    return lookup;
  }, [messages]);

  // Get the most recent user prompt (for streaming/active tool use)
  const mostRecentUserPrompt = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i].content;
      }
    }
    return '';
  }, [messages]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, currentResponse, currentThinking, isThinking, activeToolUse, pendingApprovals]);

  const toggleThinking = (messageId: string) => {
    setExpandedThinking((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  // Extract target slot from tool input, user prompt, or context
  const extractTargetSlot = (input?: Record<string, unknown>, userPrompt?: string): SlotType => {
    const resolveSlot = (key: string): SlotType | undefined => {
      if (PROFILE_SLOTS[key as SlotType]) return key as SlotType;
      if (IMAGE_KEY_TO_SLOT[key]) return IMAGE_KEY_TO_SLOT[key];
      return undefined;
    };

    if (input?.imageKey && typeof input.imageKey === 'string') {
      const slot = resolveSlot(input.imageKey);
      if (slot) return slot;
    }
    if (input?.targetField && typeof input.targetField === 'string') {
      const slot = resolveSlot(input.targetField);
      if (slot) return slot;
    }

    if (userPrompt) {
      const parsedSlot = parseSlotFromPrompt(userPrompt);
      if (parsedSlot) return parsedSlot;
    }

    if (imageGenFlow?.context?.imageKey) {
      const slot = resolveSlot(imageGenFlow.context.imageKey as string);
      if (slot) return slot;
    }

    return 'profile_image';
  };

  // Handle save for an image tool result
  const handleImageSave = useCallback(
    async (toolCallId: string, imageUrl: string, targetSlot: SlotType) => {
      setImageToolStates((prev) => ({
        ...prev,
        [toolCallId]: { ...prev[toolCallId], isSaving: true },
      }));

      try {
        const success = await saveImageDirect(targetSlot, imageUrl);

        if (success) {
          setImageToolStates((prev) => ({
            ...prev,
            [toolCallId]: { ...prev[toolCallId], isSaving: false, isSaved: true },
          }));
        } else {
          setImageToolStates((prev) => ({
            ...prev,
            [toolCallId]: { ...prev[toolCallId], isSaving: false, isSaved: false },
          }));
        }
      } catch (error) {
        console.error('[ConversationView] Error saving image:', error);
        setImageToolStates((prev) => ({
          ...prev,
          [toolCallId]: { ...prev[toolCallId], isSaving: false, isSaved: false },
        }));
      }
    },
    [saveImageDirect]
  );

  const handleImageReject = useCallback((toolCallId: string) => {
    setShowRegenerateFor(toolCallId);
  }, []);

  const handleRegenerateSelect = useCallback(
    (option: RegenerateOption, toolCallId: string) => {
      setShowRegenerateFor(null);
      setImageToolStates((prev) => {
        const newState = { ...prev };
        delete newState[toolCallId];
        return newState;
      });
      if (onRegenerate && option.prompt) {
        onRegenerate(option.prompt);
      }
    },
    [onRegenerate]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleRegenerateCancel = useCallback(() => {
    setShowRegenerateFor(null);
    if (showRegenerateFor) {
      setImageToolStates((prev) => {
        const newState = { ...prev };
        delete newState[showRegenerateFor];
        return newState;
      });
    }
  }, [showRegenerateFor]);

  const handleCustomPrompt = useCallback(() => {
    setShowRegenerateFor(null);
  }, []);

  // Handle save for inline markdown images
  const handleInlineImageSave = useCallback(
    async (imageUrl: string) => {
      const targetSlot = extractTargetSlot(
        imageGenFlow?.context as unknown as Record<string, unknown>,
        mostRecentUserPrompt
      );

      setInlineImageStates((prev) => ({
        ...prev,
        [imageUrl]: { imageUrl, isSaving: true, isSaved: false },
      }));

      try {
        const success = await saveImageDirect(targetSlot, imageUrl);

        if (success) {
          setInlineImageStates((prev) => ({
            ...prev,
            [imageUrl]: { imageUrl, isSaving: false, isSaved: true },
          }));
        } else {
          setInlineImageStates((prev) => ({
            ...prev,
            [imageUrl]: { imageUrl, isSaving: false, isSaved: false },
          }));
        }
      } catch (error) {
        console.error('[ConversationView] Error saving inline image:', error);
        setInlineImageStates((prev) => ({
          ...prev,
          [imageUrl]: { imageUrl, isSaving: false, isSaved: false },
        }));
      }
    },
    [saveImageDirect, imageGenFlow, mostRecentUserPrompt]
  );

  const handleInlineImageReject = useCallback((imageUrl: string) => {
    setShowRegenerateFor(`inline-${imageUrl}`);
  }, []);

  const handleInlineRegenerateSelect = useCallback(
    (option: RegenerateOption, imageUrl: string) => {
      setShowRegenerateFor(null);
      setInlineImageStates((prev) => {
        const newState = { ...prev };
        delete newState[imageUrl];
        return newState;
      });
      if (onRegenerate && option.prompt) {
        onRegenerate(option.prompt);
      }
    },
    [onRegenerate]
  );

  // Check if a tool use is an image tool with a result
  const isImageToolResult = (toolUse: NonNullable<SolomonAgentMessage['toolUses']>[0]): boolean => {
    return IMAGE_TOOLS.has(toolUse.tool) && !!toolUse.result && !toolUse.isError;
  };

  return (
    <div ref={scrollRef} className={cn(styles.container, className)}>
      {messages.map((message, messageIndex) => (
        <div
          key={message.id}
          className={cn(styles.message, message.role === 'user' ? styles.userMessage : styles.assistantMessage)}
        >
          <div className={styles.messageHeader}>
            <span className={styles.role}>{message.role === 'user' ? '> you' : '$ solomon'}</span>
            <span className={styles.timestamp}>
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {(() => {
            if (message.role === 'assistant') {
              const { text, images } = parseMarkdownImages(message.content);
              const userPrompt = lastUserPromptByIndex[messageIndex];
              const targetSlot = extractTargetSlot(
                imageGenFlow?.context as unknown as Record<string, unknown>,
                userPrompt
              );

              // Collect image URLs from tool results to avoid duplicates
              const toolImageUrls = new Set<string>();
              if (message.toolUses) {
                message.toolUses.forEach((toolUse) => {
                  if (isImageToolResult(toolUse)) {
                    const url = extractImageUrl(toolUse.result);
                    if (url) toolImageUrls.add(url);
                  }
                });
              }

              const uniqueImages = images.filter((img) => !toolImageUrls.has(img.url));

              return (
                <>
                  {text && <div className={styles.messageContent}>{text}</div>}
                  <InlineImages
                    images={uniqueImages}
                    targetSlot={targetSlot}
                    inlineImageStates={inlineImageStates}
                    showRegenerateFor={showRegenerateFor}
                    onSave={handleInlineImageSave}
                    onReject={handleInlineImageReject}
                    onRegenerateSelect={handleInlineRegenerateSelect}
                    onCustomPrompt={handleCustomPrompt}
                    styles={styles}
                  />
                </>
              );
            }

            return <div className={styles.messageContent}>{message.content}</div>;
          })()}

          {/* Extended thinking section */}
          {message.thinking && (
            <ThinkingSection
              messageId={message.id}
              thinking={message.thinking}
              isExpanded={expandedThinking[message.id] || false}
              onToggle={toggleThinking}
              styles={styles}
            />
          )}

          {/* Tool uses */}
          {message.toolUses && message.toolUses.length > 0 && (
            <MessageToolUses
              messageId={message.id}
              toolUses={message.toolUses}
              imageToolStates={imageToolStates}
              showRegenerateFor={showRegenerateFor}
              extractTargetSlot={extractTargetSlot}
              userPrompt={lastUserPromptByIndex[messageIndex]}
              onImageSave={handleImageSave}
              onImageReject={handleImageReject}
              onRegenerateSelect={handleRegenerateSelect}
              onCustomPrompt={handleCustomPrompt}
              styles={styles}
            />
          )}
        </div>
      ))}

      {/* Pending approval cards */}
      {pendingApprovals.map((action) => (
        <ActionApprovalCard
          key={action.id}
          action={action}
          onApprove={(token) => approveAction(token)}
          onReject={(token) => rejectPendingAction(token)}
        />
      ))}

      {/* Streaming response */}
      {isStreaming && currentResponse && <StreamingMessage response={currentResponse} styles={styles} />}

      {/* Tool use indicator - show ImageMessage for image tools */}
      {activeToolUse && IMAGE_TOOLS.has(activeToolUse.tool) && (
        <ImageMessage
          targetSlot={extractTargetSlot(activeToolUse.input, mostRecentUserPrompt)}
          isGenerating
          message={`Generating your ${
            PROFILE_SLOTS[extractTargetSlot(activeToolUse.input, mostRecentUserPrompt)]?.shortLabel || 'profile'
          } image...`}
        />
      )}

      {/* Regular tool use indicator for non-image tools */}
      {activeToolUse && !IMAGE_TOOLS.has(activeToolUse.tool) && (
        <ToolUseIndicator toolName={activeToolUse.tool} styles={styles} />
      )}

      {/* Empty state */}
      {messages.length === 0 && !isStreaming && !isThinking && <EmptyState styles={styles} />}
    </div>
  );
}

export default ConversationView;
